<template>
    <card-normal>
        <template v-if="nextAction">
            <flex-container align-items="center" :gap="Size.xs">
                <text-decorator :size="FontSize.sm">
                    Datum vervolgactie:
                </text-decorator>

                <text-decorator
                    v-if="nextAction.isToday"
                    :size="FontSize.sm"
                    bold
                >
                    Vandaag
                </text-decorator>
                <text-decorator
                    v-else
                    :size="FontSize.sm"
                >
                    {{ formatDate(nextActionDate ?? '') }}
                </text-decorator>

                <text-decorator :size="FontSize.sm">
                    {{ formatTime(nextActionDate ?? '') }} uur
                </text-decorator>

                <svg-icon
                    :icon="nextActionIcon"
                    :size="IconSize.sm"
                    :color="nextActionIconColor"
                />

                <text-decorator :size="FontSize.sm" :color="Color.gray400">
                    {{ nextAction.typeLabel ?? '' }}
                </text-decorator>
            </flex-container>

            <horizontal-line />
        </template>

        <progress-bar-group>
            <progress-bar-section
                v-for="category in categories"
                :key="category.value"
                :current="isCurrentCategory(category) && !isCompletedCategory(category)"
                :completed="isCompletedCategory(category)"
                :failed="isFailedCategory(category)"
                :percentage="percentageForCategory(category)"
            >
                <template #title>
                    {{ category.label }}
                </template>
                <template #subtext>
                    <div
                        v-if="(isCurrentCategory(category) || isCompletedCategory(category)) && entryDateForCategory(category)"
                        style="display: flex; align-items: center; gap: 10px;"
                    >
                        <svg-icon
                            :size="IconSize.sm"
                            :icon="LoginIcon"
                            :color="Color.gray400"
                        />
                        <text-decorator :size="FontSize.sm" :color="Color.gray400">
                            {{ formatDate(entryDateForCategory(category) ?? '') }}
                        </text-decorator>
                    </div>

                    <div
                        v-if="isCurrentCategory(category)"
                        style="display: flex; align-items: center; gap: 10px;"
                    >
                        <svg-icon
                            :size="IconSize.sm"
                            :icon="SyncIcon"
                            :color="Color.gray400"
                        />
                        <text-decorator :size="FontSize.sm" :color="Color.gray400">
                            {{ formatDate(props.lead?.updatedAt) }}
                        </text-decorator>
                    </div>
                </template>
            </progress-bar-section>
        </progress-bar-group>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        CardNormal,
        Color,
        FontSize,
        HorizontalLine,
        IconSize,
        ProgressBarGroup,
        ProgressBarSection,
        Size,
        SvgIcon,
        TextDecorator,
    } from '@keyvue/one-ui';

    import ApartmentIcon from '@keyvue/one-material-icons/icons/apartment.svg?component';
    import PhoneIcon from '@keyvue/one-material-icons/icons/call.svg?component';
    import VideoCamIcon from '@keyvue/one-material-icons/icons/videocam.svg?component';
    import HandshakeIcon from '@keyvue/one-material-icons/icons/handshake.svg?component';
    import MailIcon from '@keyvue/one-material-icons/icons/mail.svg?component';
    import NotificationIcon from '@keyvue/one-material-icons/icons/notifications.svg?component';
    import EuroIcon from '@keyvue/one-material-icons/icons/euro.svg?component';
    import UpgradeIcon from '@keyvue/one-material-icons/icons/upgrade.svg?component';
    import LoginIcon from '@keyvue/one-material-icons/icons/login.svg?component';
    import SyncIcon from '@keyvue/one-material-icons/icons/sync.svg?component';
    import { formatDate, formatTime } from '@/utils/date';

    import { computed } from 'vue';

    import { Appointment, AppointmentType, Lead, PhaseCategory, Status, Task, TaskType } from '@keyvue/one-api-client';
    import useLeadPageData from '@composables/useLeadPageData';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();

    const leadPageData = useLeadPageData(props.lead.id);

    const [
        appointmentCollection,
        taskOverview,
        eventLogs,
    ] = await Promise.all([
        leadPageData.appointmentCollection(),
        leadPageData.taskOverview(),
        leadPageData.eventLogs(),
    ]);

    const nextAction = computed(() => {
        const sortActionsCallback = (a, b) => {
            const aDate = a instanceof Appointment ? a.startDateTime : a.dueDateTime;
            const bDate = b instanceof Appointment ? b.startDateTime : b.dueDateTime;

            return aDate.getTime() - bDate.getTime();
        };

        // Try to find next action (either future appointment or not completed task)
        const nextActions = [
            ...appointmentCollection.data.value.filter((appointment) => !appointment.isPast),
            // A task completed in the task list changes in place, without a reload.
            ...taskOverview.active.data.value.filter((task) => !task.isCompleted),
        ].sort(sortActionsCallback);

        if (nextActions.length) {
            return nextActions[0];
        }

        // Try to find previous action. Of the completed tasks only the latest can be it, so that is the
        // only one loaded.
        const actions = [
            ...appointmentCollection.data.value,
            ...taskOverview.active.data.value,
            ...(taskOverview.latestCompleted.value ? [taskOverview.latestCompleted.value] : []),
        ].sort(sortActionsCallback);

        if (actions.length) {
            return actions[actions.length - 1];
        }

        return null;
    });

    const nextActionDate = computed(() => {
        if (!nextAction.value) {
            return undefined;
        }

        return nextAction.value instanceof Appointment ? nextAction.value.startDateTime : nextAction.value.dueDateTime;
    });

    const nextActionIcon = computed(() => {
        if (!nextAction.value) {
            return undefined;
        }

        return nextAction.value instanceof Appointment ? getAppointmentIcon(nextAction.value) : getTaskIcon(nextAction.value);
    });

    const nextActionIconColor = computed(() => {
        if (!nextAction.value) {
            return undefined;
        }

        return nextAction.value instanceof Appointment ? getAppointmentIconColor(nextAction.value) : getTaskIconColor(nextAction.value);
    });

    /** niet boos worden, eslint wilt het zo */
    const getAppointmentIcon = (appointment: Appointment) => {
        switch (appointment.type?.value) {
        case AppointmentType.PHONE.value:
            return PhoneIcon;
        case AppointmentType.VIDEO.value:
            return VideoCamIcon;
        case AppointmentType.OFFICE.value:
            return ApartmentIcon;
        case AppointmentType.CONTACT.value:
            return HandshakeIcon;
        default:
            return '';
        }
    };

    /** niet boos worden, eslint wilt het zo */
    const getAppointmentIconColor: (appointment: Appointment) => Color = (appointment: Appointment) => {
        switch (appointment.type?.value) {
        case AppointmentType.PHONE.value:
            return Color.brandPurple;
        case AppointmentType.VIDEO.value:
            return Color.brandPink;
        case AppointmentType.OFFICE.value:
            return Color.yellow500;
        case AppointmentType.CONTACT.value:
            return Color.blue500;
        default:
            return Color.gray400;
        }
    };

    /** niet boos worden, eslint wilt het zo */
    const getTaskIcon = (task: Task) => {
        switch (task.type?.value) {
        case TaskType.EMAIL.value:
        case TaskType.LETTER.value:
            return MailIcon;
        case TaskType.CALL.value:
            return PhoneIcon;
        case TaskType.NOTIFICATION.value:
        case TaskType.REMINDER.value:
            return NotificationIcon;
        case TaskType.BILLING.value:
            return EuroIcon;
        case TaskType.UPDATE.value:
            return UpgradeIcon;
        default:
            return '';
        }
    };

    /** niet boos worden, eslint wilt het zo */
    const getTaskIconColor: (task: Task) => Color = (task: Task) => {
        switch (task.type?.value) {
        case TaskType.EMAIL.value:
            return Color.blue500;
        case TaskType.LETTER.value:
            return Color.yellow500;
        case TaskType.CALL.value:
            return Color.brandPurple;
        case TaskType.NOTIFICATION.value:
        case TaskType.REMINDER.value:
            return Color.brandPink;
        case TaskType.BILLING.value:
            return Color.yellow500;
        case TaskType.UPDATE.value:
            return Color.green500;
        default:
            return Color.gray400;
        }
    };

    const categories = computed(() => PhaseCategory.keyVueValues);

    const currentCategory = computed(() => {
        let category = props.lead.phase?.category;

        // Overwrite category of current phase (if any) if status is won
        if (props.lead.status.equals(Status.WON)) {
            category = PhaseCategory.DECISION;
        }

        return category;
    });

    const isCurrentCategory = (phaseCategory: PhaseCategory) => {
        // Check if given category is current category
        if (!currentCategory.value) {
            return false;
        }

        return currentCategory.value.equals(phaseCategory);
    };

    const isCompletedCategory = (phaseCategory: PhaseCategory) => {
        // If status is won, every category is completed
        if (props.lead.status.equals(Status.WON)) {
            return true;
        }

        // Check if given category is before current category
        if (!currentCategory.value) {
            return false;
        }

        return phaseCategory.compareTo(currentCategory.value) < 0;
    };

    const isFailedCategory = (phaseCategory: PhaseCategory) => {
        // If status is not lost, no category is failed
        if (!props.lead.status.equals(Status.LOST)) {
            return false;
        }

        // Check if given category is or is after current category
        if (!currentCategory.value) {
            return false;
        }

        return phaseCategory.compareTo(currentCategory.value) >= 0;
    };

    const percentageForCategory = (phaseCategory) => {
        // If status is won, every category is completed
        if (props.lead.status.equals(Status.WON)) {
            return 100;
        }

        // Check if category is completed
        if (isCompletedCategory(phaseCategory)) {
            return 100;
        }

        // Check if category is current
        if (!isCurrentCategory(phaseCategory)) {
            return 0;
        }

        // Determine progress in current category
        const phases = computed(() => props.lead.pipeline.activePhases);
        const phasesForCategory = phases.value.filter((phase) => phase.category.equals(phaseCategory));

        if (phasesForCategory.length === 0) {
            return 0;
        }

        const currentPhase = props.lead.phase;
        const currentPhaseIndex = phasesForCategory.findIndex((phase) => phase.equals(currentPhase));

        return (currentPhaseIndex / phasesForCategory.length) * 100;
    };

    const lastStatusChangedDate = () => {
        const statusChangedEvents = eventLogs.filter((eventLog) => eventLog.event === 'ReferralStatusChangedEvent');

        if (statusChangedEvents.length === 0) {
            return null;
        }

        return statusChangedEvents[statusChangedEvents.length - 1].loggedAt;
    };

    const entryDateForCategoryFromEventLogs = (phaseCategory: PhaseCategory) => {
        const phases = computed(() => props.lead.pipeline.activePhases);
        const phasesForCategory = phases.value.filter((phase) => phase.category.equals(phaseCategory));

        const phaseChangedEventsForCategory = eventLogs
            .filter((eventLog) => eventLog.event === 'ReferralPhaseChangedEvent')
            .filter((eventLog) => {
                return phasesForCategory.some((phase) => phase.name === eventLog.eventData.phase);
            });

        if (phaseChangedEventsForCategory.length === 0) {
            return null;
        }

        return phaseChangedEventsForCategory[0].loggedAt;
    };

    const entryDateForCategory = (phaseCategory: PhaseCategory) => {
        // If category is "entry", the entry date is the created date of the lead
        if (phaseCategory.equals(PhaseCategory.ENTRY)) {
            return props.lead.createdAt;
        }

        // Determine entry date from phase change events
        let entryDate = entryDateForCategoryFromEventLogs(phaseCategory);

        // Check entry date
        if (entryDate === null) {
            // If category is "approach", the entry date is the updated date of the lead (if category is current)
            if (phaseCategory.equals(PhaseCategory.APPROACH) && isCurrentCategory(phaseCategory)) {
                entryDate = props.lead.updatedAt;
            }

            // If category is "decision", the entry date is the date of the last status change
            if (phaseCategory.equals(PhaseCategory.DECISION)) {
                entryDate = lastStatusChangedDate();
            }
        }

        return entryDate;
    };
</script>
